import React, { useRef, useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
  useLocation,
  Navigate,
} from "react-router-dom";

// --- COMPONENTS ---
import Header from "./components/Header";
import Footer from "./components/Footer";
import HomeSection from "./components/HomeSection";
import AboutSection from "./components/AboutSection";
import TestsSection from "./components/TestsSection";
import LoginPage from "./components/LoginPage";
import SignupPage from "./components/SignupPage";
import ProfilePage from "./components/ProfilePage";
import CategoryListPage from "./components/CategoryListPage";
import AssessmentsPage from "./components/AssessmentsPage";
import QuizPage from "./components/QuizPage";
import VerifyEmailPage from "./components/VerifyEmailPage";
import AdminLogin from "./components/AdminLogin";
import AdminDashboard from "./components/AdminDashboard";

// --- QUIZ PAGES ---
import BigFivePage from "./quiz/BigFivePage";
import LeftRightBrainPage from "./quiz/LeftRightBrain";
import MbtiPage from "./quiz/MbtiPage";
import SixteenPFPage from "./quiz/SixteenPFPage";
import CareerOrientationsPage from "./quiz/CareerOrientationsPage";
import RiasecPage from "./quiz/Riasec";
import DrPhils from "./quiz/DrPhils";
import Perception from "./quiz/Perception";
import DiagramAnalysis from "./quiz/DiagramAnalysis";
import IqTestPage from "./quiz/IqTestPage";
import IqQuizPage from "./quiz/IqQuizPage";
import IqResultPage from "./quiz/IqResultPage";
import EqTestPage from "./quiz/EqTestPage";
import EqQuizPage from "./quiz/EqQuizPage";
import EqResultPage from "./quiz/EqResultPage";
import CareerInterestAssessment from "./quiz/CareerInterestAssessment";


// Other Tests ---
import Geometry from "./quiz/Geometry";
import SqTest from "./quiz/SqTest";
import PqTest from "./quiz/PqTest";


// --- STYLES ---
import "./App.css";
import { isSessionActive, getSessionExpiryMs, clearSession, AUTH_EVENT } from "./utils/auth";

const HEADER_OFFSET = 70;

const ProtectedRoute = ({ isLoggedIn, children }) => {
  if (!isLoggedIn) return <Navigate to="/login" replace />;
  return children;
};

// Separate guard for the admin dashboard — gated on adminToken, not the
// regular-user isLoggedIn state, since admin auth is a completely separate
// token/flow from user auth (see handleAdminLogin below). AdminDashboard's
// own API calls still authenticate via this localStorage token (Authorization
// header), not the httpOnly cookie, so this has to stay in sync with that
// until AdminDashboard is migrated to cookie-based auth too.
const ProtectedAdminRoute = ({ children }) => {
  const isAdminLoggedIn = !!localStorage.getItem("adminToken");
  if (!isAdminLoggedIn) return <Navigate to="/admin" replace />;
  return children;
};

function MainContent() {
  const [theme, setTheme] = useState("dark");
  // The real JWT lives only in an httpOnly cookie the server manages —
  // isLoggedIn is a reactive mirror of the non-sensitive "isLoggedIn" hint
  // cookie (see utils/auth.js), kept in sync below via AUTH_EVENT and the
  // expiry timer, so every part of the app agrees on one value.
  const [isLoggedIn, setIsLoggedIn] = useState(() => isSessionActive());

  useEffect(() => {
    document.body.className = theme === "dark" ? "dark" : "";
  }, [theme]);

  const navigateRef = useRef(null);

  // Re-sync isLoggedIn when a logout happens anywhere in this tab
  // (AUTH_EVENT) or when the tab regains focus, so switching back from
  // another tab that logged out picks up the change.
  useEffect(() => {
    const syncSession = () => {
      const active = isSessionActive();
      setIsLoggedIn((prev) => (prev === active ? prev : active));
      if (!active && navigateRef.current) {
        navigateRef.current("/login");
      }
    };

    window.addEventListener(AUTH_EVENT, syncSession);
    window.addEventListener("focus", syncSession);
    document.addEventListener("visibilitychange", syncSession);
    return () => {
      window.removeEventListener(AUTH_EVENT, syncSession);
      window.removeEventListener("focus", syncSession);
      document.removeEventListener("visibilitychange", syncSession);
    };
  }, []);

  // Automatic logout the moment the current session's expiry is reached,
  // instead of only discovering it's stale on the next API call.
  useEffect(() => {
    if (!isLoggedIn) return;
    const expiryMs = getSessionExpiryMs();
    if (!expiryMs) return;

    const msRemaining = expiryMs - Date.now();
    if (msRemaining <= 0) {
      clearSession();
      setIsLoggedIn(false);
      return;
    }

    const timerId = setTimeout(() => {
      clearSession();
      setIsLoggedIn(false);
    }, msRemaining);

    return () => clearTimeout(timerId);
  }, [isLoggedIn]);

  const homeRef = useRef(null);
  const aboutRef = useRef(null);
  const testsRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  const scrollToSection = (section) => {
    let ref = null;
    switch (section) {
      case "home":
        ref = homeRef;
        break;
      case "about":
        ref = aboutRef;
        break;
      case "tests":
        ref = testsRef;
        break;
      default:
        break;
    }

    if (ref && ref.current) {
      let top = ref.current.offsetTop - HEADER_OFFSET;

      // Scroll to bottom of tests section for assessments
      if (section === "tests") {
        top = ref.current.offsetTop + ref.current.offsetHeight - window.innerHeight;
      }

      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  useEffect(() => {
    if (location.pathname === "/" && location.state?.scrollTarget) {
      scrollToSection(location.state.scrollTarget);
    }
  }, [location]);

  const handleLogin = () => {
    setIsLoggedIn(true);
  };
  const handleLogout = () => {
    clearSession();
    setIsLoggedIn(false);
    navigate("/");
  };

  // Admin login stores its own token (adminToken) so it doesn't collide
  // with a signed-in regular user's token, and doesn't touch the
  // isLoggedIn state used by ProtectedRoute for the quiz/assessment pages.
  // On success it now goes to the admin dashboard instead of "/".
  const handleAdminLogin = (data) => {
    if (data?.token) {
      localStorage.setItem("adminToken", data.token);
    }
    navigate("/admin/dashboard");
  };

  const handleAdminLogout = () => {
    localStorage.removeItem("adminToken");
    navigate("/admin");
  };

  const handleExplore = (categoryId) => {
    navigate(`/tests/${categoryId}`);
  };

  return (
    <>
      <Header
        theme={theme}
        setTheme={setTheme}
        isLoggedIn={isLoggedIn}
        showLogin={() => navigate("/login")}
        goProfile={() => navigate("/profile")}
      />
      <Routes>
        {/* HOME PAGE */}
        <Route
          path="/"
          element={
            <>
              <div ref={homeRef}>
                <HomeSection
                  onStartAssessment={() => scrollToSection("tests")}
                  onLearnMore={() => scrollToSection("about")}
                />
              </div>

              <div ref={aboutRef}>
                <AboutSection onStartAssessment={() => scrollToSection("tests")} />
              </div>

              <div ref={testsRef} style={{ minHeight: "100vh", padding: "2rem 0" }}>
                <TestsSection isLoggedIn={isLoggedIn} onExplore={handleExplore} />
              </div>
            </>
          }
        />

        {/* AUTH ROUTES */}
        <Route path="/admin" element={<AdminLogin onLogin={handleAdminLogin} />} />
        <Route
          path="/admin/dashboard"
          element={
            <ProtectedAdminRoute>
              <AdminDashboard onLogout={handleAdminLogout} />
            </ProtectedAdminRoute>
          }
        />
        <Route path="/login" element={<LoginPage onLogin={handleLogin} onSuccess={handleLogin} />} />
        <Route path="/signup" element={<SignupPage onSuccess={handleLogin} />} />
        <Route path="/profile" element={<ProfilePage onLogout={handleLogout} />} />
        <Route path="/verify-email" element={<VerifyEmailPage onLogin={handleLogin} />} />

        {/* CATEGORY & ASSESSMENTS */}
        <Route path="/tests" element={<AssessmentsPage />} />
        <Route path="/tests/:categoryId" element={
          <ProtectedRoute isLoggedIn={isLoggedIn}>
            <CategoryListPage />
          </ProtectedRoute>
        } />
        <Route path="/quiz/:quizId" element={
          <ProtectedRoute isLoggedIn={isLoggedIn}>
            <QuizPage />
          </ProtectedRoute>
        } />

        {/* PERSONALITY TESTS */}
        <Route path="/tests/personality/bigfive" element={
          <ProtectedRoute isLoggedIn={isLoggedIn}><BigFivePage /></ProtectedRoute>
        } />
        <Route path="/tests/personality/leftright" element={
          <ProtectedRoute isLoggedIn={isLoggedIn}><LeftRightBrainPage /></ProtectedRoute>
        } />
        <Route path="/tests/personality/mbti" element={
          <ProtectedRoute isLoggedIn={isLoggedIn}><MbtiPage /></ProtectedRoute>
        } />
        <Route path="/tests/personality/16pf" element={
          <ProtectedRoute isLoggedIn={isLoggedIn}><SixteenPFPage /></ProtectedRoute>
        } />
        <Route path="/tests/personality/drphils" element={
          <ProtectedRoute isLoggedIn={isLoggedIn}><DrPhils /></ProtectedRoute>
        } />
        <Route path="/tests/personality/perception" element={
          <ProtectedRoute isLoggedIn={isLoggedIn}><Perception /></ProtectedRoute>
        } />
        <Route path="/tests/personality/diagramanalysis" element={
          <ProtectedRoute isLoggedIn={isLoggedIn}><DiagramAnalysis /></ProtectedRoute>
        } />
        <Route path="/tests/personality/geometry" element={
          <ProtectedRoute isLoggedIn={isLoggedIn}><Geometry /></ProtectedRoute>
        } />
        <Route path="/tests/personality/sqtest" element={
          <ProtectedRoute isLoggedIn={isLoggedIn}><SqTest /></ProtectedRoute>
        } />
        <Route path="/tests/personality/pqtest" element={
          <ProtectedRoute isLoggedIn={isLoggedIn}><PqTest /></ProtectedRoute>
        } />
        <Route path="/tests/personality/eqtest" element={
          <ProtectedRoute isLoggedIn={isLoggedIn}><EqTestPage /></ProtectedRoute>
        } />

        {/* EQ QUIZ */}
        <Route path="/quiz/eq-test" element={
          <ProtectedRoute isLoggedIn={isLoggedIn}><EqQuizPage /></ProtectedRoute>
        } />
        <Route path="/quiz/eq-result" element={
          <ProtectedRoute isLoggedIn={isLoggedIn}><EqResultPage /></ProtectedRoute>
        } />

        {/* IQ QUIZ */}
        <Route path="/tests/career/iqtest" element={
          <ProtectedRoute isLoggedIn={isLoggedIn}><IqTestPage /></ProtectedRoute>
        } />
        <Route path="/quiz/iq-test" element={
          <ProtectedRoute isLoggedIn={isLoggedIn}><IqQuizPage /></ProtectedRoute>
        } />
        <Route path="/quiz/iq-result" element={
          <ProtectedRoute isLoggedIn={isLoggedIn}><IqResultPage /></ProtectedRoute>
        } />

        {/* CAREER TESTS */}
        <Route path="/tests/career/riasec" element={
          <ProtectedRoute isLoggedIn={isLoggedIn}><RiasecPage /></ProtectedRoute>
        } />
        <Route path="/tests/career/career-anchor" element={
          <ProtectedRoute isLoggedIn={isLoggedIn}><CareerOrientationsPage /></ProtectedRoute>
        } />
        <Route path="/tests/career/career-interest" element={
          <ProtectedRoute isLoggedIn={isLoggedIn}><CareerInterestAssessment /></ProtectedRoute>
        } />

      </Routes>

      {!["/login", "/signup"].includes(location.pathname) && <Footer />}
    </>
  );
}

function App() {
  return (
    <Router>
      <MainContent />
    </Router>
  );
}

export default App;